import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  private cardVisibility = new BehaviorSubject<boolean>(false);
  cardVisibility$ = this.cardVisibility.asObservable();

  private selectedKid = new BehaviorSubject<any>(null); // Use type `any` to allow full object
  selectedKid$ = this.selectedKid.asObservable();

  private selectedExam = new BehaviorSubject<any>(null);
  selectedExam$ = this.selectedExam.asObservable();

  private selectedYear = new BehaviorSubject<number>(new Date().getFullYear()); // Default to current year
  selectedYear$ = this.selectedYear.asObservable();

  private menuOpenSubject = new BehaviorSubject<boolean>(false);
  menuOpen$ = this.menuOpenSubject.asObservable();

  private menuItemsSubject = new BehaviorSubject<any[]>([]); // Subject to hold menu items
  menuItems$ = this.menuItemsSubject.asObservable();

  private schoolListSubject = new BehaviorSubject<string[]>(this.getStoredSchoolList());
  schoolList$ = this.schoolListSubject.asObservable();

  private paymentListSource = new BehaviorSubject<any[]>([]);
  paymentList$ = this.paymentListSource.asObservable();

  private currentRoleSubject = new BehaviorSubject<string>(
    localStorage.getItem('activeRole') || 'Admin'
  );
  private staffCheckedInSubject = new BehaviorSubject<boolean>(this.getStoredStaffCheckIn());
  staffCheckedIn$ = this.staffCheckedInSubject.asObservable();

  private getStoredSchoolList(): string[] {
    const stored = localStorage.getItem("selectedSchoolList");
    return stored ? JSON.parse(stored) : [];
  }

  showCard(isVisible: boolean): void {
    this.cardVisibility.next(isVisible);
  }

  setSelectedKid(kid: any): void {
    this.selectedKid.next(kid); // Pass the entire kid object
  }

  setSelectedExam(exam: any): void {
    this.selectedExam.next(exam);
  }

  setSelectedYear(year: number): void {
    this.selectedYear.next(year); // Update the selected year
  }

  toggleMenu() {
    console.log('Toggle menu called');
    this.menuOpenSubject.next(!this.menuOpenSubject.value);
  }

  closeMenu() {
    this.menuOpenSubject.next(false);
  }

  sendMenuItems(menuItems: any[]) {
    this.menuItemsSubject.next(menuItems);
  }

  setCurrentRole(role: string): void {
    localStorage.setItem('activeRole', role);
    this.currentRoleSubject.next(role);
  }

  getCurrentRole(): Observable<string> {
    return this.currentRoleSubject.asObservable();
  }

  getActiveRole(): string {
    return localStorage.getItem('activeRole') || 'Admin';
  }

  getBasePathForRole(role: string | null | undefined): string {
    const normalized = (role || '').toLowerCase();
    if (normalized === 'staff') {
      return 'staff';
    }
    if (normalized === 'parent') {
      return 'parent';
    }
    return 'admin';
  }

  setStaffCheckedIn(status: boolean): void {
    localStorage.setItem('isCheckedIn', JSON.stringify(status));
    this.staffCheckedInSubject.next(status);
  }

  getStaffCheckedIn(): boolean {
    return this.staffCheckedInSubject.value;
  }

  getActiveRoleBasePath(): string {
    return this.getBasePathForRole(this.getActiveRole());
  }

  buildRoleBasedSegments(...segments: (string | number)[]): any[] {
    return [this.getActiveRoleBasePath(), ...segments];
  }

  buildRoleBasedUrl(path: string): string {
    const base = this.getActiveRoleBasePath();
    const cleaned = path.startsWith('/') ? path.substring(1) : path;
    return `/${base}/${cleaned}`;
  }

  buildRoleBasedUrlFor(role: string, path: string): string {
    const base = this.getBasePathForRole(role);
    const cleaned = path.startsWith('/') ? path.substring(1) : path;
    return `/${base}/${cleaned}`.replace(/\/+/g, '/');
  }

  getDefaultRouteForRole(role: string): { segments: (string | number)[]; queryParams?: Record<string, any> } {
    const normalized = (role || '').toLowerCase();

    if (normalized === 'staff') {
      return { segments: ['dashboard'] };
    }

    if (normalized === 'parent') {
      return { segments: ['dashboard'] };
    }

    return { segments: ['dashboard'] };
  }

  getUserRoles(): any[] {
    const userRoles = localStorage.getItem('userRoles');
    return userRoles ? JSON.parse(userRoles) : [];
  }

  getUserAccess(): any[] {
    const userAccess = localStorage.getItem('userAccess');
    return userAccess ? JSON.parse(JSON.parse(userAccess)) : [];
  }

  setSchoolList(schoolList: string[]): void {
    localStorage.setItem("selectedSchoolList", JSON.stringify(schoolList));
    this.schoolListSubject.next(schoolList);
  }

  setPaymentList(paymentList: any[]) {
    this.paymentListSource.next(paymentList);
  }

  private getStoredStaffCheckIn(): boolean {
    const stored = localStorage.getItem('isCheckedIn');
    if (!stored) {
      return false;
    }
    try {
      return JSON.parse(stored) === true;
    } catch {
      return false;
    }
  }
}
